import { useCallback, useEffect, useMemo, useState } from "react";
import API from "../../api/api";
import BatchActionBar from "../../components/BatchActionBar";
import { Bell, Send, Users, Globe, CheckCircle2, RefreshCcw } from "lucide-react";

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString();
};

export default function Notifications() {
  const [targetType, setTargetType] = useState("all");
  const [userIds, setUserIds] = useState("");
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [dataJson, setDataJson] = useState('{"screen":"home"}');
  const [loadingSend, setLoadingSend] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState("");

  // Multi-delete state
  const [selectedIds, setSelectedIds] = useState([]);
  const [isBatchDeleting, setIsBatchDeleting] = useState(false);

  const fetchNotifications = useCallback(async (nextPage = page) => {
    setLoadingList(true);
    try {
      const response = await API.get("/admin/notifications", {
        params: { page: nextPage, limit: 15 },
      });
      setNotifications(response.data?.notifications || []);
      setUnreadCount(response.data?.unreadCount || 0);
      setTotalPages(response.data?.totalPages || 1);
      setPage(response.data?.page || 1);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load notifications");
    } finally {
      setLoadingList(false);
    }
  }, [page]);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await API.get("/admin/users", { params: { limit: 1000 } });
      setUsers(response.data.users || []);
    } catch (err) {
      console.error("Failed to fetch users:", err);
    }
  }, []);

  useEffect(() => {
    fetchNotifications(page);
  }, [fetchNotifications, page]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleToggleSelectAll = () => {
    if (selectedIds.length === notifications.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(notifications.map((n) => n._id));
    }
  };

  const handleToggleSelectRow = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleBatchDelete = async () => {
    try {
      setIsBatchDeleting(true);
      const { data } = await API.post("/admin/batch-delete", {
        entity: "notifications",
        ids: selectedIds,
      });
      if (data?.success) {
        setSelectedIds([]);
        setSuccess("Batch delete completed");
        fetchNotifications(page);
      }
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Batch delete failed");
    } finally {
      setIsBatchDeleting(false);
    }
  };

  const stats = useMemo(() => {
    const sent = notifications.filter((item) => item.deliveryStatus === "sent").length;
    const failed = notifications.filter((item) => item.deliveryStatus === "failed").length;
    return { sent, failed };
  }, [notifications]);

  const handleSend = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    let payloadData = {};
    try {
      payloadData = dataJson ? JSON.parse(dataJson) : {};
    } catch {
      setError("Data JSON is invalid");
      return;
    }

    setLoadingSend(true);
    try {
      const payload = {
        title,
        message,
        targetType,
        data: payloadData,
      };

      if (targetType === "users") {
        if (selectedUser) {
          payload.userIds = [selectedUser];
        } else if (userIds) {
          payload.userIds = userIds
            .split(",")
            .map((id) => id.trim())
            .filter(Boolean);
        } else {
          setError("Please select a user");
          setLoadingSend(false);
          return;
        }
      }

      const response = await API.post("/admin/notifications/send", payload);
      setSuccess(
        `Notification sent successfully! Success: ${response.data?.successCount || 0}, Failed: ${response.data?.failureCount || 0
        }`
      );
      setTitle("");
      setMessage("");
      setDataJson('{"screen":"home"}');
      setSelectedUser("");
      setUserIds("");
      fetchNotifications(1);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to send notification");
    } finally {
      setLoadingSend(false);
    }
  };

  const markAllRead = async () => {
    try {
      await API.patch("/admin/notifications/read-all");
      setNotifications((prev) => prev.map((item) => ({ ...item, isRead: true })));
      setUnreadCount(0);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to mark all as read");
    }
  };

  const markSingleRead = async (id) => {
    try {
      await API.patch(`/admin/notifications/${id}/read`);
      setNotifications((prev) =>
        prev.map((item) => (item._id === id ? { ...item, isRead: true } : item))
      );
      setUnreadCount((prev) => Math.max(prev - 1, 0));
    } catch (err) {
      setError(err.response?.data?.message || "Failed to mark notification as read");
    }
  };

  return (
    <div className="space-y-6 lg:pl-5 xl:pl-7 2xl:pl-0">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Push & In-App Notifications</h1>
          <p className="text-sm text-gray-500">Send push messages to users and monitor sent logs</p>
        </div>
        <button
          onClick={() => fetchNotifications(page)}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gray-100 dark:bg-slate-700 text-sm font-medium hover:bg-gray-200 dark:hover:bg-slate-600"
        >
          <RefreshCcw size={16} /> Refresh
        </button>
      </div>

      {error && (
        <div className="p-3 text-sm rounded-lg bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {success && (
        <div className="p-3 text-sm rounded-lg bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300">
          {success}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-4">
          <p className="text-xs text-gray-500 dark:text-gray-400">Total in Page</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{notifications.length}</p>
        </div>
        <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-4">
          <p className="text-xs text-gray-500 dark:text-gray-400">Unread</p>
          <p className="text-2xl font-bold text-amber-500">{unreadCount}</p>
        </div>
        <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-4">
          <p className="text-xs text-gray-500 dark:text-gray-400">Sent Delivery</p>
          <p className="text-2xl font-bold text-emerald-500">{stats.sent}</p>
        </div>
        <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-4">
          <p className="text-xs text-gray-500 dark:text-gray-400">Failed Delivery</p>
          <p className="text-2xl font-bold text-rose-500">{stats.failed}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
        <div className="xl:col-span-2 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-5">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Compose Push Notification</h2>
          <form onSubmit={handleSend} className="space-y-4">
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setTargetType("all")}
                className={`px-3 py-2 rounded-lg text-sm border ${targetType === "all"
                  ? "bg-blue-600 text-white border-blue-600"
                  : "border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-200"
                  }`}
              >
                <span className="inline-flex items-center gap-1"><Globe size={14} /> All Users</span>
              </button>
              <button
                type="button"
                onClick={() => setTargetType("users")}
                className={`px-3 py-2 rounded-lg text-sm border ${targetType === "users"
                  ? "bg-blue-600 text-white border-blue-600"
                  : "border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-200"
                  }`}
              >
                <span className="inline-flex items-center gap-1"><Users size={14} /> Selected Users</span>
              </button>
            </div>

            {targetType === "users" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Select User
                </label>

                <select
                  value={selectedUser}
                  onChange={(e) => setSelectedUser(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 dark:bg-blue-600 dark:text-white dark:border-slate-600 bg-transparent px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">-- Select User --</option>

                  {users.map((user) => (
                    <option key={user._id} value={user._id} className="dark:bg-[#334155] dark:text-white">
                      {user.name} ({user.phone || user.username || "No detail"})
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Title</label>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                className="w-full rounded-lg border border-gray-300 dark:border-slate-600 bg-transparent px-3 py-2 text-sm"
                placeholder="Special Offer"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Message</label>
              <textarea
                rows={3}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                required
                className="w-full rounded-lg border border-gray-300 dark:border-slate-600 bg-transparent px-3 py-2 text-sm"
                placeholder="You received 100 bonus coins"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Data (JSON)</label>
              <textarea
                rows={3}
                value={dataJson}
                onChange={(e) => setDataJson(e.target.value)}
                className="w-full rounded-lg border border-gray-300 dark:border-slate-600 bg-transparent px-3 py-2 text-sm font-mono"
              />
            </div>

            <button
              type="submit"
              disabled={loadingSend}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold disabled:opacity-70"
            >
              <Send size={16} /> {loadingSend ? "Sending..." : "Send Notification"}
            </button>
          </form>
        </div>

        <div className="xl:col-span-3 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              {notifications.length > 0 && (
                <label className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={notifications.length > 0 && selectedIds.length === notifications.length}
                    onChange={handleToggleSelectAll}
                    className="w-4 h-4 rounded border-gray-300 dark:border-slate-600 text-blue-600 focus:ring-blue-500 cursor-pointer"
                  />
                  Select All
                </label>
              )}
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Notification Alerts</h2>
            </div>
            <button
              onClick={markAllRead}
              className="inline-flex items-center gap-2 text-sm px-3 py-1.5 rounded-md bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300"
            >
              <CheckCircle2 size={14} /> Mark All Read
            </button>
          </div>

          <div className="space-y-3 max-h-[560px] overflow-y-auto pr-1">
            {loadingList && <p className="text-sm text-gray-500">Loading notifications...</p>}
            {!loadingList && notifications.length === 0 && (
              <div className="text-sm text-gray-500">No notifications found.</div>
            )}
            {notifications.map((item) => {
              const isSelected = selectedIds.includes(item._id);
              return (
                <div
                  key={item._id}
                  className={`rounded-lg border p-3 transition-colors ${isSelected
                    ? "border-blue-500 bg-blue-50/70 dark:bg-blue-500/10"
                    : item.isRead
                      ? "border-gray-200 dark:border-slate-700"
                      : "border-blue-300 dark:border-blue-500/40 bg-blue-50/40 dark:bg-blue-500/5"
                    }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-2">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleToggleSelectRow(item._id)}
                        className="mt-1 w-4 h-4 rounded border-gray-300 dark:border-slate-600 text-blue-600 focus:ring-blue-500 cursor-pointer"
                      />
                      <Bell size={16} className="mt-1 text-blue-500 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-sm text-gray-900 dark:text-white">{item.title}</p>
                        <p className="text-sm text-gray-600 dark:text-gray-300">{item.message}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {item.type} • {item.targetType || "-"} • {formatDate(item.createdAt)}
                        </p>
                        {(item.successCount > 0 || item.failureCount > 0) && (
                          <p className="text-xs text-gray-500 mt-1">
                            sent {item.successCount || 0} • failed {item.failureCount || 0}
                          </p>
                        )}
                      </div>
                    </div>
                    {!item.isRead && (
                      <button
                        onClick={() => markSingleRead(item._id)}
                        className="text-xs px-2 py-1 rounded-md bg-blue-600 text-white"
                      >
                        Mark Read
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-4 flex items-center justify-between text-sm">
            <button
              onClick={() => fetchNotifications(Math.max(1, page - 1))}
              disabled={page <= 1}
              className="px-3 py-1.5 rounded border border-gray-300 dark:text-white dark:border-slate-600 disabled:opacity-50"
            >
              Prev
            </button>
            <span className="text-gray-500">Page {page} / {totalPages}</span>
            <button
              onClick={() => fetchNotifications(Math.min(totalPages, page + 1))}
              disabled={page >= totalPages}
              className="px-3 py-1.5 rounded border border-gray-300 dark:border-slate-600 dark:text-white disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Floating Multi-Delete Bar */}
      <BatchActionBar
        selectedCount={selectedIds.length}
        totalCount={notifications.length}
        onClearSelection={() => setSelectedIds([])}
        onSelectAll={handleToggleSelectAll}
        onDeleteSelected={handleBatchDelete}
        isDeleting={isBatchDeleting}
        entityName="notifications"
      />
    </div>
  );
}
